import React, { useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import cx from 'classnames'
import { useIntl } from 'intl'

import Glider from 'glider-js'
import { Icon } from 'components/dataDisplay'

import messages from './messages'
import s from './Carousel.scss'


// More info about the options https://nickpiscitelli.github.io/Glider.js/#settings
export type CarouselOptions = {
  slidesToShow?: number | 'auto'
  slidesToScroll?: number | 'auto' // if auto it will match slidesToShow
  itemWidth?: number // ignored unless slidesToShow is set to auto
  exactWidth?: boolean // ignored unless slidesToShow is set to auto
  scrollLock?: boolean // if true, it will scroll to the nearest slide after any scroll interactions
  scrollLockDelay?: number
  resizeLock?: boolean // if true, it will lock to the nearest slide on resizing of the window
  rewind?: boolean // if true, it will scroll to the beginning/end when its respective endpoint is reached
  draggable?: boolean // if true, it can be scrolled by click with the mouse
  duration?: number // default: 0.5 An aggravator used to control animation speed. Higher is slower!
}

export type CarouselEvents = {
  // add events if needed https://nickpiscitelli.github.io/Glider.js/#events
  onSlideVisible?: (event: CustomEvent<{ slide: number }>) => void
}

export type CarouselRef = {
  scrollItem: typeof Glider.scrollItem
}

export type CarouselProps = CarouselOptions & CarouselEvents & {
  children: React.ReactNode
  // ATTN we use only one className for container. If you need to override some properties use glider-* global classes
  className?: string
  withArrows?: boolean
  prevIcon?: React.ReactNode
  nextIcon?: React.ReactNode
  withDots?: boolean
  autoplay?: boolean
  ignoreInitialEvents?: boolean
  autoplaySpeed?: number
  'data-testid'?: string
}

// TODO: Investigate why memoizing a parent component leads to loss of dots control — added on 21-04-2023 by algeas
const Carousel = React.forwardRef<CarouselRef, CarouselProps>((props, forwardedRef) => {
  const {
    children,
    className,
    withArrows,
    prevIcon,
    nextIcon,
    withDots,
    // options
    slidesToShow = 1,
    slidesToScroll = 1,
    itemWidth,
    exactWidth,
    scrollLock = true,
    scrollLockDelay = 150,
    resizeLock = true,
    rewind = false,
    draggable,
    duration = true,
    // autoplay
    autoplay,
    autoplaySpeed = 2500,
    // events
    ignoreInitialEvents,
    onSlideVisible,
    'data-testid': dataTestId,
  } = props

  const carouselRef = useRef<HTMLDivElement>(null)
  const slidesRef = useRef<HTMLDivElement>(null)
  const prevArrowRef = useRef<HTMLButtonElement>(null)
  const nextArrowRef = useRef<HTMLButtonElement>(null)
  const dotsRef = useRef<HTMLDivElement>(null)

  const gliderRef = useRef<Glider>(null)

  useImperativeHandle(forwardedRef, () => ({
    scrollItem: (index: number) => {
      gliderRef.current?.scrollItem(index)
    },
  }))

  // Events
  useEffect(() => {
    const slidesElement = slidesRef.current

    // All events are mounted to the slides element
    if (!slidesElement) {
      return
    }

    const events = {
      'glider-slide-visible': (event) => {
        if (ignoreInitialEvents && event.timeStamp > 1000) {
          onSlideVisible(event)
        }
      },
    }

    Object.keys(events).map((event) => {
      const handler = events[event]

      if (handler) {
        slidesElement.addEventListener(event, handler)
      }
    })

    return () => {
      Object.keys(events).map((event) => {
        const handler = events[event]

        if (handler) {
          slidesElement.removeEventListener(event, handler)
        }
      })
    }
  }, [ ignoreInitialEvents, onSlideVisible ])

  // Library initialization
  useEffect(() => {
    if (!slidesRef.current) {
      return
    }

    const slidesElement = slidesRef.current

    gliderRef.current = new Glider(slidesElement, {
      skipTrack: true,
    })

    // helps to hide broken layout
    slidesElement.classList.add('glider-created')

    return () => {
      slidesElement.classList.remove('glider-created')
      gliderRef.current.destroy()
    }
  }, [])

  // Options update when props are updated
  useEffect(() => {
    if (!gliderRef.current) {
      return
    }

    gliderRef.current.setOption({
      // default options
      slidesToShow,
      slidesToScroll,
      itemWidth,
      exactWidth,
      scrollLock,
      scrollLockDelay,
      resizeLock,
      rewind,
      draggable,
      duration,
      //
      dots: !withDots ? null : dotsRef.current,
      arrows: !withArrows ? null : {
        prev: prevArrowRef.current,
        next: nextArrowRef.current,
      },
    }, true)

    gliderRef.current.refresh(true)
  }, [
    withDots, withArrows, children, slidesToShow, slidesToScroll, itemWidth, exactWidth,
    scrollLock, scrollLockDelay, resizeLock, rewind, draggable, duration,
  ])

  // Autoplay
  useEffect(() => {
    const carouselElement = carouselRef.current

    if (!autoplay || !carouselElement) {
      return
    }

    let autoplayInterval = null

    const start = () => {
      if (autoplayInterval === null) {
        autoplayInterval = setInterval(() => {
          if (gliderRef.current) {
            gliderRef.current.scrollItem('next')
          }
        }, autoplaySpeed)
      }
    }

    const stop = () => {
      if (autoplayInterval !== null) {
        clearInterval(autoplayInterval)
        autoplayInterval = null
      }
    }

    carouselElement.addEventListener('mouseover', stop)
    carouselElement.addEventListener('touchstart', stop)
    carouselElement.addEventListener('mouseout', start)
    carouselElement.addEventListener('touchcancel', start)
    carouselElement.addEventListener('touchend', start)
    start()

    return () => {
      stop()
      carouselElement.removeEventListener('mouseover', stop)
      carouselElement.removeEventListener('touchstart', stop)
      carouselElement.removeEventListener('mouseout', start)
      carouselElement.removeEventListener('touchcancel', start)
      carouselElement.removeEventListener('touchend', start)
    }
  }, [ autoplay, autoplaySpeed ])

  const intl = useIntl()

  const {
    prevLabel,
    nextLabel,
  } = useMemo(() => ({
    prevLabel: intl.formatMessage(messages.prev),
    nextLabel: intl.formatMessage(messages.next),
  }), [ intl ])

  const { trackStyle, slideStyle } = useMemo(() => {
    if (!itemWidth) {
      return {}
    }

    // this styles are needed to support the same appearance before carousel is initialized
    // the will be replaced by glider-js
    return {
      trackStyle: {
        width: itemWidth * React.Children.count(children),
      },
      slideStyle: {
        width: itemWidth,
      },
    }
  }, [ itemWidth, children ])

  const carouselClassName = cx(className, 'relative', s.carousel)

  return (
    <div ref={carouselRef} className={carouselClassName} data-testid={dataTestId}>
      <div ref={slidesRef} className="glider">
        <div className="glider-track" style={trackStyle}>
          {
            React.Children.map(children, (child) => (
              <div className="glider-slide" style={slideStyle}>
                {child}
              </div>
            ))
          }
        </div>
      </div>
      {
        Boolean(withArrows) && (
          <>
            <button ref={prevArrowRef} className="glider-prev" aria-label={prevLabel}>
              {prevIcon || <Icon name="32/arrow-left-in-circle" />}
            </button>
            <button ref={nextArrowRef} className="glider-next" aria-label={nextLabel}>
              {nextIcon || <Icon name="32/arrow-right-in-circle" />}
            </button>
          </>
        )
      }
      {
        Boolean(withDots) && (
          <div ref={dotsRef} className="glider-dots" role="tablist" />
        )
      }
    </div>
  )
})


export default React.memo(Carousel)
